import * as React from 'react';
import { Column } from '@tanstack/react-table';

import { cn } from '#/lib/utils';
import { Badge } from './badge';
import { Button } from './button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator
} from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Separator } from './separator';
import { CheckIcon, PlusCircleIcon } from 'lucide-react';
import { OptionTreeItemType } from 'types/common';
import { Icons } from '../icons';

export interface DataTableTreeFacetedFilter<TData, TValue> {
  column?: Column<TData, TValue>;
  title?: string;
  options: OptionTreeItemType[];
}

export function DataTableTreeFacetedFilter<TData, TValue>({
  column,
  title,
  options
}: DataTableTreeFacetedFilter<TData, TValue>) {
  const selectedValues = new Set(column?.getFilterValue() as string[]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 border-dashed">
          <PlusCircleIcon className="mr-2 h-4 w-4" />
          {title}
          {selectedValues?.size > 0 && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <Badge
                variant="secondary"
                className="rounded-sm px-1 font-normal lg:hidden"
              >
                {selectedValues.size}
              </Badge>
              <div className="hidden space-x-1 lg:flex">
                {selectedValues.size > 2 ? (
                  <Badge
                    variant="secondary"
                    className="rounded-sm px-1 font-normal"
                  >
                    {selectedValues.size}건 선택됨
                  </Badge>
                ) : (
                  options
                    .reduce((p: OptionTreeItemType[], l) => {
                      p.push(...getAllItems(l));
                      return p;
                    }, [])
                    .filter((option) => selectedValues.has(option.value))
                    .map((option) => (
                      <Badge
                        variant="secondary"
                        key={option.value}
                        className="rounded-sm px-1 font-normal"
                      >
                        {option.label}
                      </Badge>
                    ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-full" align="start">
        <Command>
          <CommandInput placeholder={title} />
          <CommandList>
            <CommandEmpty>검색 결과가 없습니다.</CommandEmpty>
            <CommandGroup>
              {options?.map((o) =>
                getAllDescendants(o, selectedValues, (node) => {
                  const isSelected = selectedValues.has(node.value);
                  const children = getAllItems(node);
                  children.forEach((c) => {
                    if (isSelected) {
                      selectedValues.delete(c.value);
                    } else {
                      selectedValues.add(c.value);
                    }
                  });
                  const filterValues = Array.from(selectedValues);
                  column?.setFilterValue(
                    filterValues.length ? filterValues : undefined
                  );
                })
              )}
            </CommandGroup>
            {selectedValues.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => column?.setFilterValue(undefined)}
                    className="justify-center text-center"
                  >
                    필터 제거
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function getAllItems(node: OptionTreeItemType) {
  const nodes: OptionTreeItemType[] = [node];
  for (var i = 0; i < node.children.length; i++) {
    var child = node.children[i];
    nodes.push(...getAllItems(child));
  }
  return nodes;
}

function getAllDescendants(
  node: OptionTreeItemType,
  selectedValues: Set<string>,
  onSelect: (node: OptionTreeItemType) => void,
  depth: number = 0
) {
  const nodes: React.ReactNode[] = [
    getNode(node, selectedValues, onSelect, depth)
  ];
  for (var i = 0; i < node.children.length; i++) {
    var child = node.children[i];
    nodes.push(
      ...getAllDescendants(child, selectedValues, onSelect, depth + 1)
    );
  }
  return nodes;
}

function getNode(
  node: OptionTreeItemType,
  selectedValues: Set<string>,
  onSelect: (node: OptionTreeItemType) => void,
  depth: number
) {
  const isSelected = selectedValues.has(node.value);
  return (
    <CommandItem
      key={node.value}
      value={node.label}
      onSelect={() => onSelect(node)}
    >
      <div
        className={cn(
          'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
          isSelected
            ? 'bg-primary text-primary-foreground'
            : 'opacity-50 [&_svg]:invisible'
        )}
      >
        <CheckIcon className={cn('h-4 w-4')} />
      </div>
      <div
        className="flex items-center"
        style={{
          paddingLeft: `${depth}rem`
        }}
      >
        {node.children.length ? (
          <Icons.folder className="w-4 h-4 mr-2" />
        ) : (
          <Icons.post className="w-4 h-4 mr-2" />
        )}
        {node.label}
      </div>
    </CommandItem>
  );
}
